import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import openMailtoPreferNewTab from '../utils/openMailtoPreferNewTab';

/** Unique emails, preserve order, case-insensitive (same idea as Client Letters mailto). */
function dedupeEmailsPreserveOrder(addresses) {
  const seen = new Set();
  const out = [];
  for (const raw of addresses || []) {
    const e = (raw || '').trim();
    if (!e) continue;
    const key = e.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  return out;
}

/** Use plain text in mailto body; strip minimal HTML if the template stored tags. */
function bodyPlainForMailto(body) {
  if (body == null) return '';
  const s = String(body);
  if (!/<[a-z][\s\S]*>/i.test(s)) return s;
  return s
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function buildAtPacketExternalMailtoHref({ toList, subject, body }) {
  const toDeduped = dedupeEmailsPreserveOrder(toList);
  const toEncoded = toDeduped.map((e) => encodeURIComponent(e)).join(';');
  const params = [
    `subject=${encodeURIComponent(subject || '')}`,
    `body=${encodeURIComponent(bodyPlainForMailto(body))}`,
  ];
  const qs = params.join('&');
  return toEncoded ? `mailto:${toEncoded}?${qs}` : `mailto:?${qs}`;
}

/** Plain-text email bodies lose newlines in HTML; detect HTML vs text and format for preview. */
function bodyHtmlForPreview(body) {
  if (body == null || body === '') return '<p>(empty)</p>';
  const s = String(body);
  if (/<[a-z][\s\S]*>/i.test(s)) {
    return s;
  }
  const escaped = s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  return `<div class="at-packet-email-preview-plain">${escaped.replace(/\r\n|\r|\n/g, '<br />')}</div>`;
}

/**
 * Shown when backend returns external_email preview (AT_PACKET_EXTERNAL_EMAIL_PREVIEW_MODE).
 * User opens their default mail client (mailto) to send PHI from their own mailbox.
 */
function AtPacketExternalEmailPreviewModal({
  open,
  packetId,
  subject,
  body,
  recipientsPreview,
  statusCode,
  onDismiss,
  /** Called after mailto opens successfully; parent should close modal and show guidance. */
  onSendComplete,
}) {
  const [openError, setOpenError] = useState(null);

  const mailtoHref = useMemo(
    () =>
      buildAtPacketExternalMailtoHref({
        toList: recipientsPreview,
        subject,
        body,
      }),
    [recipientsPreview, subject, body]
  );

  if (!open || !packetId) return null;

  const recipientLine = Array.isArray(recipientsPreview)
    ? recipientsPreview.filter(Boolean).join(', ')
    : '';

  const handleOpenInEmail = () => {
    setOpenError(null);
    const ok = openMailtoPreferNewTab(mailtoHref);
    if (!ok) {
      setOpenError('Could not open your email app. Check your default mail program in system settings.');
      return;
    }
    onSendComplete?.();
  };

  return createPortal(
    <div className="at-packet-email-preview-overlay" role="dialog" aria-modal="true" aria-labelledby="at-packet-email-preview-title">
      <div className="at-packet-email-preview-dialog">
        <h2 id="at-packet-email-preview-title" className="at-packet-email-preview-title">
          Preview external email
          {statusCode ? ` (${statusCode})` : ''}
        </h2>
        <p className="at-packet-email-preview-intro">
          The packet was saved. Nothing has been emailed yet. Review below, then open in your email app to send securely from your own mailbox, or close this dialog.
        </p>
        {recipientLine && (
          <div className="at-packet-email-preview-field">
            <span className="at-packet-email-preview-label">To</span>
            <div className="at-packet-email-preview-recipients">{recipientLine}</div>
          </div>
        )}
        <div className="at-packet-email-preview-field">
          <label className="at-packet-email-preview-label" htmlFor="at-packet-email-preview-subject">Subject</label>
          <input id="at-packet-email-preview-subject" type="text" readOnly className="at-packet-email-preview-subject" value={subject || ''} />
        </div>
        <div className="at-packet-email-preview-field">
          <span className="at-packet-email-preview-label">Body</span>
          <div
            className="at-packet-email-preview-body"
            // Tenant-controlled template output; staff-only preview.
            dangerouslySetInnerHTML={{ __html: bodyHtmlForPreview(body) }}
          />
        </div>
        {openError && <p className="at-packets-error">{openError}</p>}
        <div className="at-packet-email-preview-actions">
          <button type="button" className="btn-cancel" onClick={onDismiss}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" onClick={handleOpenInEmail}>
            Open in email
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default AtPacketExternalEmailPreviewModal;
